import type React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag as TagIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface TagPageLinkProps {
  tag: { id: string; name: string };
  /**
   * Called after the link is activated. Lets a host overlay that outlives the
   * click — the touch tag-detail popover — close itself on the way out.
   */
  onNavigate?: () => void;
  className?: string;
}

/**
 * The single affordance for "open this tag's page" (`/tags/:tagId`).
 *
 * The cross-entity tag page has existed since the tags module shipped, but every
 * link to it used to be buried: two inside the {@link TagChip} hover overlay
 * (one of them labelled "Edit tag", which it isn't — the page is read-only for
 * non-admins) and one row click on the admin-only governance list. The picker
 * and the filter offered none, so in practice the page was unreachable. This
 * component is that link, spelled once and reused everywhere a tag is shown.
 *
 * It wears the `Tag` glyph deliberately: that is the icon `TagDetailPage` puts
 * in its own `PageHeader`, so the link carries the mark of what it opens.
 *
 * **Safe inside a `CommandItem`.** cmdk fires `onSelect` from the item's own
 * React `onClick`, so stopping propagation here is enough to navigate without
 * also picking the tag. It is likewise safe inside a clickable row or card.
 */
export function TagPageLink({ tag, onNavigate, className }: TagPageLinkProps) {
  const { t } = useTranslation('common');
  const label = t('tags.viewTagPage', { name: tag.name });

  const handleClick = (e: React.MouseEvent) => {
    // Only stop bubbling — never preventDefault, which would cancel the
    // navigation this link exists for.
    e.stopPropagation();
    onNavigate?.();
  };

  return (
    <Link
      to={`/tags/${tag.id}`}
      aria-label={label}
      title={label}
      onClick={handleClick}
      className={cn(
        // Coarse-pointer 44px target, reserved as real layout rather than bled
        // over the neighbouring control. Same contract as `InfoTooltip`, and the
        // numbers are its 12px-glyph pair: a 12px box reserves (44-12)/2 = 16 a
        // side, and the `::after` reaches exactly that far because this trigger
        // is `border-0 p-0` (padding box == border box). Add padding or a border
        // and both numbers have to move.
        "relative touch:after:absolute touch:after:content-['']",
        'touch:m-[16px] touch:after:-inset-[16px]',
        'inline-flex flex-shrink-0 items-center justify-center rounded-sm border-0 p-0',
        'text-muted-foreground transition-colors hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        className,
      )}
    >
      <TagIcon className="h-3 w-3" />
    </Link>
  );
}
